import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS, API_CONFIG } from '../../config/api';
import TMLogo from '../TMLogo';
import teBg from '../../assets/te_background.png';

type Step = 'credentials' | 'mfa_setup' | 'mfa_verify';

interface LoginFormProps {
  onLoginSuccess?: (data: any) => void;
  // Renders just the card (no full-screen background/logo chrome), for use
  // inside SessionLockOverlay where the actual page is still visible behind it.
  embedded?: boolean;
}

// Defined outside LoginForm so React never unmounts it on re-render (preserves input focus)
const Shell: React.FC<{ error: string | null; embedded?: boolean; children: React.ReactNode }> = ({ error, embedded, children }) => {
  const card = (
    <div className="card bg-base-100 shadow-xl w-full max-w-md">
      <div className="card-body gap-6">
        {!embedded && (
          <div className="text-center flex flex-col items-center gap-2">
            <TMLogo className="h-28 w-auto tm-logo-tone" />
            <p className="text-primary font-semibold tracking-wide text-sm">A.I.-driven Clinical Management for modern healthcare</p>
          </div>
        )}
        {error && (
          <div className="alert alert-error text-sm">
            <span>{error}</span>
          </div>
        )}
        {children}
        {!embedded && (
          <p className="text-center text-base-content/40 text-xs pt-2">
            Copyright &copy; 2026 Christian Bannard. All rights reserved.
          </p>
        )}
      </div>
    </div>
  );

  if (embedded) return card;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 gap-4 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${teBg})` }}>
      {card}
    </div>
  );
};

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess, embedded }) => {
  const { login } = useAuth();

  const [step, setStep] = useState<Step>('credentials');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaSession, setMfaSession] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totpInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus the TOTP input when we transition to an MFA step
  useEffect(() => {
    if (step !== 'credentials') {
      setTimeout(() => totpInputRef.current?.focus(), 100);
    }
  }, [step]);

  // When setup step starts, fetch the QR code
  useEffect(() => {
    if (step === 'mfa_setup' && mfaSession) {
      fetchQrCode();
    }
  }, [step, mfaSession]);

  const fetchQrCode = async () => {
    try {
      const url = `${API_CONFIG.FULL_BASE}${ENDPOINTS.AUTH.MFA_SETUP}?mfa_session=${mfaSession}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error('Failed to load QR code');
      const data = await resp.json();
      setQrCode(data.qr_code);
      setSecret(data.secret);
    } catch {
      setError('Could not load QR code. Please try again.');
    }
  };

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Username and password are required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await apiCall(ENDPOINTS.AUTH.LOGIN, {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || 'Invalid credentials');
        return;
      }

      if (data.mfa_setup_required) {
        setMfaSession(data.mfa_session);
        setStep('mfa_setup');
      } else if (data.mfa_required) {
        setMfaSession(data.mfa_session);
        setStep('mfa_verify');
      } else {
        // Regular user — token issued directly
        login(data.token, data.user);
        onLoginSuccess?.(data);
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const submitMfaCode = async (code: string) => {
    if (code.length !== 6 || isLoading) return;
    setIsLoading(true);
    setError(null);

    const endpoint = step === 'mfa_setup'
      ? ENDPOINTS.AUTH.MFA_SETUP_CONFIRM
      : ENDPOINTS.AUTH.MFA_VERIFY;

    try {
      const resp = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify({ mfa_session: mfaSession, totp_code: code }),
      });
      const data = await resp.json();

      if (!resp.ok) {
        setError(data.error || 'Invalid code');
        setTotpCode('');
        totpInputRef.current?.focus();
        return;
      }

      login(data.token, data.user);
      onLoginSuccess?.(data);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitMfaCode(totpCode);
  };

  const handleTotpInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(val);
    if (error) setError(null);
    if (val.length === 6) submitMfaCode(val);
  };

  // ── step: username + password ────────────────────────────────────────────
  if (step === 'credentials') {
    return (
      <Shell error={error} embedded={embedded}>
        <form className="space-y-6" onSubmit={handleCredentialsSubmit}>
          <div className="space-y-4">
            <div className="form-control">
              <label htmlFor="username" className="label">
                <span className="label-text">Username</span>
              </label>
              <input
                id="username" name="username" type="text"
                autoComplete="username" required
                value={username}
                onChange={e => { setUsername(e.target.value); setError(null); }}
                className="input input-bordered w-full"
                placeholder="Enter your username"
              />
            </div>
            <div className="form-control">
              <label htmlFor="password" className="label">
                <span className="label-text">Password</span>
              </label>
              <input
                id="password" name="password" type="password"
                autoComplete="current-password" required
                value={password}
                onChange={e => { setPassword(e.target.value); setError(null); }}
                className="input input-bordered w-full"
                placeholder="Enter your password"
              />
            </div>
          </div>
          <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
            {isLoading ? <><span className="loading loading-spinner" />Signing in...</> : 'Sign in'}
          </button>
        </form>
      </Shell>
    );
  }

  // ── step: first-time TOTP setup ─────────────────────────────────────────
  if (step === 'mfa_setup') {
    return (
      <Shell error={error} embedded={embedded}>
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold">Set up Google Authenticator</h2>
          <p className="text-sm text-base-content opacity-70">
            Your account requires two-factor authentication. Scan the QR code with
            Google Authenticator, then enter the 6-digit code below.
          </p>
        </div>

        {qrCode ? (
          <div className="flex flex-col items-center gap-3">
            <img src={qrCode} alt="Google Authenticator QR code" className="w-48 h-48 border border-base-300 rounded-lg" />
            <details className="text-xs text-base-content opacity-60 cursor-pointer">
              <summary>Can't scan? Enter the code manually</summary>
              <p className="mt-1 font-mono break-all select-all bg-base-200 rounded p-2">{secret}</p>
            </details>
          </div>
        ) : (
          <div className="flex justify-center">
            <span className="loading loading-spinner loading-lg" />
          </div>
        )}

        <form onSubmit={handleMfaSubmit} className="space-y-4">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Verification code</span>
            </label>
            <input
              ref={totpInputRef}
              type="text" inputMode="numeric" pattern="\d{6}"
              maxLength={6} placeholder="000000"
              value={totpCode} onChange={handleTotpInput}
              className="input input-bordered w-full text-center text-2xl tracking-widest font-mono"
            />
          </div>
          <button type="submit" disabled={isLoading || totpCode.length !== 6} className="btn btn-primary w-full">
            {isLoading ? <><span className="loading loading-spinner" />Verifying...</> : 'Confirm and sign in'}
          </button>
          <button type="button" className="btn btn-ghost btn-sm w-full"
            onClick={() => { setStep('credentials'); setTotpCode(''); setError(null); }}>
            Back to login
          </button>
        </form>
      </Shell>
    );
  }

  // ── step: TOTP verify (existing confirmed device) ───────────────────────
  return (
    <Shell error={error} embedded={embedded}>
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Two-Factor Authentication</h2>
        <p className="text-sm text-base-content opacity-70">
          Open Google Authenticator and enter the 6-digit code for Templar Edge.
        </p>
      </div>

      <form onSubmit={handleMfaSubmit} className="space-y-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text">Authenticator code</span>
          </label>
          <input
            ref={totpInputRef}
            type="text" inputMode="numeric" pattern="\d{6}"
            maxLength={6} placeholder="000000"
            value={totpCode} onChange={handleTotpInput}
            className="input input-bordered w-full text-center text-2xl tracking-widest font-mono"
          />
        </div>
        <button type="submit" disabled={isLoading || totpCode.length !== 6} className="btn btn-primary w-full">
          {isLoading ? <><span className="loading loading-spinner" />Verifying...</> : 'Verify and sign in'}
        </button>
        <button type="button" className="btn btn-ghost btn-sm w-full"
          onClick={() => { setStep('credentials'); setTotpCode(''); setError(null); }}>
          Back to login
        </button>
      </form>
    </Shell>
  );
};

export default LoginForm;
